import PropTypes from 'prop-types';
import React from 'react';
import {
  BaseCard,
  BaseCardBody,
  BaseCardHeader,
  Button,
} from '@unite-us/app-components';
import { phones as phonesUtils } from '@unite-us/client-utils';
import { compact, wget } from 'lodash';
import { MEDICAID_SYSTEM_TYPE } from 'common/display/NewItemStepper/constants';
import { epochToDate } from 'common/utils/utils';
import Address from 'common/display/Address';
import PhoneNumber from 'common/display/PhoneNumberDisplay';
import { FocusLink, ListField, SingleField } from 'common/display/NewItemStepper/components/ContactCard/index';
import { getAdditionalInfoDisplay } from 'common/display/Profile/CommunicationAdditionalInfo/utils';
import { connect } from 'react-redux';

const capitalizeWords = (str) => {
  if (!str) return '';
  return str.split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

const getMatchStatusPhrase = (hasCriteria, matchValue) => {
  if (!hasCriteria || matchValue === undefined) {
    return null;
  }
  return matchValue ? 'Matches your search criteria' : 'Does not match your search criteria';
};

const hasAddressDetails = (address = {}) => {
  if (!address) {
    return false;
  }
  return [
    address.line_1,
    address.city,
    address.state,
    address.postal_code,
  ].some(Boolean);
};

const buildCommunicationDetails = ({ contactCommunicationTypesEnums, phone }) => (
  getAdditionalInfoDisplay({
    communicationTypes: phone.acceptable_communication_types,
    enums: contactCommunicationTypesEnums,
    isPrimary: phone.is_primary,
  })
);

const formatPhoneForA11y = (phone = {}, contactCommunicationTypesEnums = []) => {
  if (!phone || !phone.phone_number) {
    return null;
  }

  const formattedNumber = phonesUtils.formatPhoneNumber(phone.phone_number);
  const header = compact([
    phone.phone_type,
    buildCommunicationDetails({ contactCommunicationTypesEnums, phone }),
  ]).join(' ');

  const extensionText = phone.extension ? `extension ${phone.extension}` : '';
  const numberWithExtension = compact([formattedNumber, extensionText]).join(' ');

  return compact([header, numberWithExtension]).join(' ');
};

const buildMedicaidAccessibleLabel = (medicaidIds = []) => {
  const formattedIds = medicaidIds
    .map((id) => id?.value)
    .filter(Boolean);

  return formattedIds.length ? `Medicaid ID ${formattedIds.join('; ')}` : 'Medicaid ID';
};

const buildPhoneAccessibleLabel = (phones = [], contactCommunicationTypesEnums = []) => {
  const formattedPhones = (phones || [])
    .map((phone) => formatPhoneForA11y(phone, contactCommunicationTypesEnums))
    .filter(Boolean);

  if (!formattedPhones.length) {
    return 'Phone information';
  }

  return `Phone ${formattedPhones.join('; ')}`;
};

const formatAddressForA11y = (address = {}) => {
  if (!hasAddressDetails(address)) {
    return null;
  }

  const tags = compact([
    address.address_type,
    address.is_primary ? 'primary' : null,
  ]).join(' ');

  const cityState = compact([address.city, address.state]).join(', ');
  const locationLine = compact([cityState, address.postal_code]).join(' ');

  const body = compact([
    address.line_1,
    address.line_2,
    locationLine,
    address.county ? `${address.county} county` : null,
  ]).join(', ');

  return compact([tags, body]).join(', ');
};

const buildAddressAccessibleLabel = (addresses = []) => {
  const formattedAddresses = (addresses || [])
    .map(formatAddressForA11y)
    .filter(Boolean);

  if (!formattedAddresses.length) {
    return 'Address information';
  }

  return `Address ${formattedAddresses.join('; ')}`;
};

const MedicaidIdText = ({ item }) => {
  if (!item.value) { return null; }

  return <p>{item.value}</p>;
};

MedicaidIdText.propTypes = {
  item: PropTypes.object.isRequired,
};

const PhoneNumberText = ({ item, shortDisplay }) => (
  <PhoneNumber
    item={item}
    shortDisplay={shortDisplay}
    noLink
    typeDisplayInline
  />
);

PhoneNumberText.propTypes = {
  item: PropTypes.object.isRequired,
  shortDisplay: PropTypes.bool,
};

PhoneNumberText.defaultProps = {
  shortDisplay: false,
};

const buildCardAccessibleLabel = (hasCriteria, overallMatch, indexNumber, displayName) => {
  const overallMatchPhrase = getMatchStatusPhrase(hasCriteria, overallMatch);
  const cardLabelParts = [];
  const recordPositionPrefix = indexNumber !== undefined ? `${indexNumber + 1}. ` : '';

  cardLabelParts.push(`${recordPositionPrefix}Existing record ${displayName}`.trim());

  if (overallMatchPhrase) {
    cardLabelParts.push(overallMatchPhrase);
  }

  return cardLabelParts.join(', ');
};

const buildDateOfBirthAccessibleLabel = (dateOfBirth, hasCriteria, dateOfBirthMatch) => {
  const dateOfBirthValue = epochToDate(dateOfBirth);
  const dateOfBirthPhrase = getMatchStatusPhrase(hasCriteria, dateOfBirthMatch);
  const dateOfBirthLabelPieces = [`Date of Birth ${dateOfBirthValue}`.trim()];

  if (dateOfBirthPhrase) {
    dateOfBirthLabelPieces.push(dateOfBirthPhrase);
  }

  return dateOfBirthLabelPieces.join(', ');
};

const ContactCard = ({
  id,
  contact,
  onSelectContact,
  indexNumber,
  matchDetails,
  contactCommunicationTypesEnums,
}) => {
  const {
    hasCriteria = false,
    overall: overallMatch,
    fields: {
      dateOfBirth: dateOfBirthMatch,
    } = {},
  } = matchDetails || {};

  const fallbackName = [contact.first_name, contact.last_name].filter(Boolean).join(' ');
  const displayName = capitalizeWords(contact.full_name || fallbackName || 'Client');
  const headerText = indexNumber !== undefined ? `${indexNumber + 1}: ${displayName}` : displayName;
  const cardAriaLabel = buildCardAccessibleLabel(hasCriteria, overallMatch, indexNumber, displayName);

  const medicaidIds = (contact?.identifiers?.filter(({ system_type }) => system_type === MEDICAID_SYSTEM_TYPE) || []);

  const fields = [
    {
      idPrefix: `${id}-date`,
      ariaLabel: buildDateOfBirthAccessibleLabel(contact.date_of_birth, hasCriteria, dateOfBirthMatch),
      label: 'Date of Birth',
      value: epochToDate(contact.date_of_birth),
    },
    {
      idPrefix: `${id}-medicaid`,
      ariaLabel: buildMedicaidAccessibleLabel(medicaidIds),
      label: 'Medicaid ID',
      value: medicaidIds,
      listElement: MedicaidIdText,
    },
    {
      idPrefix: `${id}-phone`,
      ariaLabel: buildPhoneAccessibleLabel(contact.phone_numbers, contactCommunicationTypesEnums),
      label: 'Phone',
      value: contact.phone_numbers,
      listElement: PhoneNumberText,
    },
    {
      idPrefix: `${id}-address`,
      ariaLabel: buildAddressAccessibleLabel(contact.addresses),
      label: 'Address',
      value: contact.addresses,
      listElement: Address,
      simpleListShortDisplay: true,
    },
  ];

  const mappedFields = fields.map((field) => {
    if (!field.listElement) {
      return (
        <FocusLink
          key={field.idPrefix}
          id={`${field.idPrefix}-link`}
          ariaLabel={field.ariaLabel}
        >
          <SingleField label={field.label} value={field.value} />
        </FocusLink>
      );
    }

    return (
      <FocusLink
        key={field.idPrefix}
        id={`${field.idPrefix}-link`}
        ariaLabel={field.ariaLabel}
      >
        <ListField
          idPrefix={field.idPrefix}
          label={field.label}
          value={field.value}
          listElement={field.listElement}
          shortDisplay={field.shortDisplay}
        />
      </FocusLink>
    );
  });

  return (
    <li className="contact-card focus-outline" role="article" aria-label={cardAriaLabel}>
      <BaseCard>
        <BaseCardHeader
          title={(
            <FocusLink
              id={`${id}-summary-link`}
              styleClasses="contact-card__focus-link contact-card__focus-link--name"
              ariaLabel={cardAriaLabel}
            >
              <strong aria-hidden="true">{headerText}</strong>
            </FocusLink>
          )}
        />
        <BaseCardBody withPadding>

          { mappedFields }

          <Button
            id={`${id}-use-btn`}
            className="contact-card__confirmation"
            label="Use This Record"
            onClick={onSelectContact(contact)}
            primary
            ariaExpanded={null}
          />
        </BaseCardBody>
      </BaseCard>
    </li>
  );
};

ContactCard.propTypes = {
  contact: PropTypes.shape({
    id: PropTypes.string,
    full_name: PropTypes.string,
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    date_of_birth: PropTypes.number,
    phone_numbers: PropTypes.array,
    addresses: PropTypes.array,
    identifiers: PropTypes.array,
  }).isRequired,
  id: PropTypes.string.isRequired,
  onSelectContact: PropTypes.func.isRequired,
  indexNumber: PropTypes.number,
  matchDetails: PropTypes.shape({
    hasCriteria: PropTypes.bool,
    overall: PropTypes.bool,
    fields: PropTypes.shape({
      dateOfBirth: PropTypes.bool,
      firstName: PropTypes.bool,
      lastName: PropTypes.bool,
    }),
  }),
  contactCommunicationTypesEnums: PropTypes.array,
};

ContactCard.defaultProps = {
  indexNumber: undefined,
  matchDetails: null,
  contactCommunicationTypesEnums: [],
};

function mapStateToProps(state) {
  return {
    contactCommunicationTypesEnums: wget(
      state,
      'session.enums.contact_data.communication_types',
      [],
    ),
  };
}

export default connect(mapStateToProps)(ContactCard);
